import React, { useState } from "react";
import { createPortal } from "react-dom";
import DOMPurify from "dompurify";
import { ResourceDto } from "../types/resources";
import { downloadCompletionBadge } from "../utils/downloadCmsAsset";
import "../styles/resources.css";

interface CompletionModalProps {
    resource: ResourceDto;
    onClose: () => void;
}

export default function CompletionModal({ resource, onClose }: CompletionModalProps) {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const sanitizedContent = DOMPurify.sanitize(resource.courseCompletionModalContent);

    const handleDownload = async () => {
        setDownloadError(null);
        setIsDownloading(true);
        try {
            await downloadCompletionBadge(
                resource.courseCompletionModalDownloadUrl,
                resource.courseCompletionModalBadgeUrl
            );
        } catch (error) {
            setDownloadError(
                error instanceof Error ? error.message : "Unable to download badge. Please try again."
            );
        } finally {
            setIsDownloading(false);
        }
    };

    return createPortal(
        <div className="resource-modal-overlay" onClick={onClose}>
            <div className="resource-modal completion-modal" onClick={(e) => e.stopPropagation()}>
                <button className="resource-modal-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
                <div className="resource-modal-content">
                    <h2 className="resource-modal-heading">{resource.courseCompletionModalHeading}</h2>

                    {resource.courseCompletionModalIconUrl && (
                        <div className="completion-icon">
                            <img src={resource.courseCompletionModalIconUrl} alt="Completion icon" />
                        </div>
                    )}

                    {resource.courseCompletionModalBadgeUrl && (
                        <div className="completion-badge">
                            <img src={resource.courseCompletionModalBadgeUrl} alt="Achievement badge" />
                        </div>
                    )}

                    <div
                        className="resource-modal-body"
                        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                    />

                    <div className="resource-modal-actions">
                        {downloadError && (
                            <p className="resource-modal-download-error" role="alert">
                                {downloadError}
                            </p>
                        )}
                        {(resource.courseCompletionModalDownloadUrl || resource.courseCompletionModalBadgeUrl) && (
                            <button
                                className="resource-modal-download-btn"
                                onClick={handleDownload}
                                disabled={isDownloading}
                            >
                                {isDownloading ? "Downloading..." : resource.courseCompletionModalLinkText}
                            </button>
                        )}
                        <button className="resource-modal-close-btn" onClick={onClose}>
                            {resource.courseCompletionModalCloseLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

